





























































import { defineComponent, watch, ref, onMounted, onBeforeUnmount } from '@vue/composition-api';
import OlbButton from '../button/button.vue';
import OlbIcon from '../icon/icon.vue';
import OlbNotice from '../notice/notice.vue';
import axios from 'axios';
import Vue from 'vue';
import PortalVue from 'portal-vue';
Vue.use(PortalVue);

/**
 * Reusable dialog component. Contains an optional title,
 * an optional cross in the upper right corner to close it and content below it,
 * which becomes scrollable depending on its amount.
 * Can also be closed by clicking on the background.
 * The dialog is triggered on click by handing over e.g. a button in the appropriate slot.
 */
export default defineComponent({
	props: {
		/**
		 * If this prop is given, that means the content is loaded asynchronous via axios
		 * and then inserted into the dialog's content.
		 */
		href: {
			type: String,
		},
		/**
		 * The dialog can be opened and closed by setting this variable.
		 * E.g. when the dialog should be open initially, the variable should be set to true.
		 */
		open: {
			type: Boolean,
			default: false,
			required: false,
		},
		/**
		 * If true, the cross in the upper right corner
		 * (which can close the dialog on click) is not rendered.
		 */
		hideCloseBtn: {
			type: Boolean,
		},

		/**
		 * If true, the background is not clickable and therefore the dialog cannot be
		 * closed by clicking on the background.
		 */
		disabledBg: {
			type: Boolean,
		},

		/**
		 * If the user wants to style the overlay specifically (e.g. its position), the handed string is applied as class to the overlay container.
		 */
		overlayClass: {
			type: [String, Array],
		},

		/**
		 * Defines how often the dialog has to be opened
		 */
		views: {
			type: String,
		},
		itemid: {
			type: String,
		},
		topBarText: {
			type: String,
		},
		topBarColor: {
			type: String,
		},
		narrow: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		OlbButton,
		OlbIcon,
		OlbNotice,
	},

	setup(props, { emit }) {
		const dialogContent = ref<HTMLDivElement | null>();
		const loading = ref(false);
		const isOpen = ref(props.open);
		const topBar = ref();
		const localStorageCountName = 'numberofviews';
		const parsedViews: number = +(props.views ? parseInt(props.views) : 0);

		/**
		 * When loading asynchronous content fails: display an error message within the dialog content.
		 */
		function loadErrorMessage() {
			const div = document.createElement('div');
			div.classList.add('py-4', 'px-2');
			const errorMessage = document.createElement('p');
			errorMessage.classList.add('text-error');
			errorMessage.textContent =
				'Es ist ein Fehler beim Laden des Inhalts aufgetreten. Bitte laden Sie die Seite neu oder versuchen Sie es zu einem späteren Zeitpunkt erneut.';
			div.appendChild(errorMessage);
			dialogContent.value?.appendChild(div);
			loading.value = false;
			isOpen.value = true;
		}

		function loadAsynchronousContent() {
			if (props.href) {
				axios
					.get(props.href)
					.then((res) => {
						if (res.status > 200) {
							loadErrorMessage();
							return;
						}
						if (res.data) {
							// insert the loaded data into the dialog's content
							const content = document.createElement('div');
							content.innerHTML = res.data;
							dialogContent.value?.appendChild(content);
							loading.value = false;
							isOpen.value = true;
						}
					})
					.catch(() => {
						loadErrorMessage();
					});
			}
		}

		function openDialog() {
			if (sessionStorage.getItem(`${localStorageCountName}Set_${props.itemid}`) !== null) {
				return false;
			}
			return (localStorage.getItem(`${localStorageCountName}_${props.itemid}`) || 0) < parsedViews;
		}

		function toggleDialog() {
			if (props.views) {
				sessionStorage.setItem(`${localStorageCountName}Set_${props.itemid}`, 'true');
				if (localStorage.getItem(`${localStorageCountName}_${props.itemid}`) === null) {
					localStorage.setItem(`${localStorageCountName}_${props.itemid}`, '1');
				} else {
					// Count up number of views and set in localStorage
					let countViews: number = +(localStorage.getItem(`${localStorageCountName}_${props.itemid}`) || 0);
					countViews++;
					localStorage.setItem(`${localStorageCountName}_${props.itemid}`, countViews.toString());
				}
			}
			// when the dialog is opened for the first time, the dialog's content is still empty and a href is given:
			// load the content asynchronously from that href
			if (!isOpen.value && props.href && !dialogContent.value?.childNodes.length) {
				loading.value = true;
				loadAsynchronousContent();
				return;
			}
			isOpen.value = !isOpen.value;
			if (!isOpen.value) {
				emit('close');
			}
		}

		watch(
			() => props.open,
			() => {
				isOpen.value = props.open;
			},
		);

		function handleBgClick(event: Event) {
			// if the background doesn't close the dialog on purpose,
			// emit an event, so an own event can be dispatched
			emit('bgclick', event);
		}

		function emitRenderEvent(element: HTMLElement) {
			// necessary to dispatch JS event due to listening in location.js (in SC folder Location)
			let dialogRenderedEvent;
			// if phrase necessary due to IE11 compatibility (see https://stackoverflow.com/questions/27176983/dispatchevent-in-chrome-but-not-in-ie11)
			if (typeof Event === 'function') {
				dialogRenderedEvent = new Event('dialog-rendered');
			} else {
				dialogRenderedEvent = document.createEvent('Event');
				dialogRenderedEvent.initEvent('dialog-rendered', true, true);
			}
			element.dispatchEvent(dialogRenderedEvent);
			emit('opened', dialogRenderedEvent);
		}

		// close the dialog if it is open and the user presses the escape key
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === 'Escape' && isOpen.value) {
				toggleDialog();
			}
		}

		onMounted(() => {
			window.addEventListener('keydown', handleKeyDown);
			if (props.topBarText) {
				document.querySelector('.olb-menu-trigger')?.addEventListener('click', () => {
					const topBarHeight = (topBar.value as HTMLDivElement).clientHeight;
					if (window.innerWidth < 1024 && window.scrollY < topBarHeight) {
						window.scrollTo({
							left: 0,
							top: topBarHeight,
							behavior: 'smooth',
						});
					}
				});
			}
		});

		onBeforeUnmount(() => {
			window.removeEventListener('keydown', handleKeyDown);
		});

		return {
			isOpen,
			loading,
			toggleDialog,
			handleBgClick,
			emitRenderEvent,
			dialogContent,
			openDialog,
			topBar,
		};
	},
});
